import { Trans } from 'react-i18next';
import { Icon } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import React from 'react';

export default class QuotaInput extends React.Component {
    constructor(...args) {
        super(...args);

        let { quota, defaultValue } = this.props;
        let value = quota ? quota.total : defaultValue;

        this.state = {
            error: null,
            lastValue: value,
            value,
            quota
        };

        let currentQuotaTotal = quota ? quota.total : 0;

        this.partialSum = this.props.allQuotas.reduce((accumulator, currentQuota) => accumulator + currentQuota.total, 0) - currentQuotaTotal;

        this.quotaMin = quota ? quota.getTotalBookings : 0;
    }

    /**
     * Handles changing the value in real-time (keydown|up)
     * @param {number} value - The new value for the quota
     */
    handleChange(value) {
        this.setState({
            value
        });
    }

    /**
     * Handles changing the value on blur
     * @returns {undefined}
     */
    onChange() {
        let { value } = this.state;

        value = +value;

        if (value < this.quotaMin) {
            return this.setState({
                error: this.renderError('quotas_mustNotBeTooSmall')
            });
        }

        if (isNaN(value) && value !== '') {
            return this.setState({
                error: this.renderError('quotas_mustBeANumber')
            });
        }

        let { maximumCapacity } = this.props;
        if (this.partialSum + value > maximumCapacity) {
            this.setState({
                error: this.renderError('quotas_mustNotBeTooBig', {
                    sum: this.partialSum + value,
                    capacity: maximumCapacity
                })
            });
            return this.props.onChange(value);
        }

        this.setState({
            error: null,
            lastValue: value
        });

        return this.props.onChange(value);
    }

    /**
     * Should the field be disabled ?
     * @returns {bool} If the input needs to be disabled
     */
    isDisabled() {
        let { currentRates, quota, zone } = this.props;

        if (!zone) {
            return false;
        }

        if (zone.quotas && zone.quotas.length) {
            let existingQuota = zone.quotas.find(rateQuota => {
                for (let rate of currentRates) {
                    if (rateQuota.rates.indexOf(rate.uuid) > -1 && (quota ? rateQuota.id !== quota.id : true)) {
                        return true;
                    }
                }

                return false;
            });

            return !!existingQuota;
        }

        return false;
    }

    /**
     * Should the field be disabled because rate/zone do not share a category?
     * @returns {bool} If the input needs to be disabled
     */
    isDisabledForCategory() {
        let { currentRates, zone } = this.props;

        if (!zone || !zone.inventories) {
            return false;
        }

        if (!Object.keys(zone.inventories).includes(this.props.currentInventory.id())) {
            return false;
        }

        let categories = zone.inventories[this.props.currentInventory.id()].categories;

        for (let rate of currentRates) {
            if (categories.indexOf(rate.category) === -1) {
                return true;
            }
        }

        return false;
    }

    /**
     * Renders an error object
     * @param {string} translationKey - Key to use when translating the error message
     * @param {Object} [props={}] - Props to pass to translation engine
     * @returns {React.Node} Error div
     */
    renderError(translationKey, props = {}) {
        return (
            <div className="error-small">
                <Trans i18nKey={translationKey} values={props}>
                    An error occurred
                </Trans>
            </div>
        );
    }

    /**
     * Deletes the current quota
     */
    deleteQuota() {
        if (!this.state.quota.id) {
            return;
        }

        this.props.deleteQuota(this.state.quota);
        this.setState({
            value: '-',
            quota: null
        });
    }

    /**
     * Return value for controlled input
     */
    getInnerValue() {
        return this.state.value;
    }

    /**
     * Handle input focus
     */
    onFocus() {
        if (this.props.quota && (!this.state.value || this.state.value === '-')) {
            this.setState({
                value: this.props.quota.total
            });
        }

        if (this.state.value === '-') {
            this.setState({
                value: ''
            });
        }
    }

    render() {
        if (this.isDisabled()) {
            return (
                <div className="error-small">
                    <Trans i18nKey="quotas_alreadyInZone">Another quota for this zone and one of the selected rates already exists</Trans>
                </div>
            );
        }

        if (this.isDisabledForCategory()) {
            return (
                <div className="error-small">
                    <Trans i18nKey="quotas_categoryDoesNotMatchForZone">This category cannot be applied to this zone</Trans>
                </div>
            );
        }

        return (
            <React.Fragment>
                <div className="ui action input quota-input">
                    <input
                        className="quota-input-input"
                        error={this.state.error}
                        onFocus={() => this.onFocus()}
                        onBlur={() => this.onChange()}
                        onChange={event => this.handleChange(event.target.value)}
                        value={this.getInnerValue()}
                    />
                    <button className="quota-input-button" disabled={!this.state.quota || !this.state.quota.id} onClick={() => this.deleteQuota()}>
                        <Icon name="delete" />
                    </button>
                </div>
                {this.state.error}
            </React.Fragment>
        );
    }
}

QuotaInput.defaultProps = {
    allQuotas: [],
    defaultValue: '-',
    quota: null
};

QuotaInput.propTypes = {
    allQuotas: PropTypes.array.isRequired,
    currentRates: PropTypes.array.isRequired,
    defaultValue: PropTypes.oneOf([PropTypes.number, '-']),
    deleteQuota: PropTypes.func.isRequired,
    maximumCapacity: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    quota: PropTypes.object,
    zone: PropTypes.object,
    currentInventory: PropTypes.object
};
